import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { BlotterTableColumnSelector, useDynamicCallback } from '@talos/kyoko';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { ALL_OPTION_FIELDS, DEFAULT_OPTION_COLUMNS_IS_VISIBLE, getOptionColumnLabel } from '../types';
import { getVisibilityMappingForMarket } from '../utils';
import { updateColumnDefsVisibility } from './filterSlice';
import { selectColumnVisibility, selectOptionMarketSelected } from './selectors';

const abstractOptionColumns: ColDef[] = ALL_OPTION_FIELDS.map(f => ({
  field: f,
  headerName: getOptionColumnLabel(f),
})).filter(f => f.field !== 'Position');

export function OptionColumnSelector({ panelID }: { panelID: string }) {
  const visibility = useAppStateSelector(state => selectColumnVisibility(state, panelID));
  const dispatch = useAppStateDispatch();
  const updateVisibility = useDynamicCallback((newVisibility: Record<(typeof ALL_OPTION_FIELDS)[number], boolean>) => {
    newVisibility.Position = true;
    dispatch(updateColumnDefsVisibility({ panelID, columnDefsVisibility: newVisibility }));
  });

  const selectedMarketName = useAppStateSelector(state => selectOptionMarketSelected(state, panelID));

  /** For some markets, we only support showing certain columns. Here we make sure that any unsupported columns are not selectable as options */
  const marketAwareColumns = useMemo(() => {
    const visibilityMapping = getVisibilityMappingForMarket(selectedMarketName);

    if (visibilityMapping) {
      return abstractOptionColumns.filter(c => c.field && visibilityMapping[c.field as keyof typeof visibilityMapping]);
    }

    return abstractOptionColumns;
  }, [selectedMarketName]);

  return (
    <BlotterTableColumnSelector
      visibility={visibility}
      columns={marketAwareColumns}
      onChange={updateVisibility}
      defaultVisibility={DEFAULT_OPTION_COLUMNS_IS_VISIBLE}
    />
  );
}
